// ** React Import
import { forwardRef, memo, useCallback, useEffect, useState } from 'react'

// ** MUI Imports
import { styled, TextField } from '@mui/material'

// **Component Imports
import RequiredLabelComponent from '../required-label-component'

// **Utils Imports
import { hexToRGBA } from '../../utils/utils'

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  alignItems: 'flex-start',
  '& .MuiInputLabel-root': {
    transform: 'none',
    lineHeight: 1.154,
    position: 'relative',
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.body2.fontSize,
    color: `${theme.palette.text.primary} !important`
  },
  '& .MuiInputBase-root': {
    borderRadius: 8,
    backgroundColor: 'transparent !important',
    border: `1px solid ${hexToRGBA(theme.palette.customColors.main, 0.2)}`,
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
      duration: theme.transitions.duration.shorter
    }),
    '&:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error):hover': {
      borderColor: `${hexToRGBA(theme.palette.customColors.main, 0.28)}`
    },
    '&:before, &:after': {
      display: 'none'
    },
    '&.MuiInputBase-sizeSmall': {
      borderRadius: 6
    },
    '&.Mui-error': {
      borderColor: theme.palette.error.main
    },
    '&.Mui-focused': {
      boxShadow: theme.shadows[2],
      '& .MuiInputBase-input:not(.MuiInputBase-readOnly):not([readonly])::placeholder':
        {
          transform: 'translateX(4px)'
        },
      '&.MuiInputBase-colorPrimary': {
        borderColor: theme.palette.primary.main
      },
      '&.MuiInputBase-colorSecondary': {
        borderColor: theme.palette.secondary.main
      },
      '&.MuiInputBase-colorInfo': {
        borderColor: theme.palette.info.main
      },
      '&.MuiInputBase-colorSuccess': {
        borderColor: theme.palette.success.main
      },
      '&.MuiInputBase-colorWarning': {
        borderColor: theme.palette.warning.main
      },
      '&.MuiInputBase-colorError': {
        borderColor: theme.palette.error.main
      },
      '&.Mui-error': {
        borderColor: theme.palette.error.main
      }
    },
    '&.Mui-disabled': {
      backgroundColor: `${theme.palette.action.selected} !important`
    },
    '& .MuiInputAdornment-root': {
      marginTop: '0 !important'
    }
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.secondary,
    '&:not(textarea)': {
      padding: '15.5px 13px'
    },
    '&:not(textarea).MuiInputBase-inputSizeSmall': {
      padding: '7.5px 32px 7.5px 13px'
    },
    '&:not(.MuiInputBase-readOnly):not([readonly])::placeholder': {
      transition: theme.transitions.create(['opacity', 'transform'], {
        duration: theme.transitions.duration.shorter
      })
    },

    // ** For Autocomplete
    '&.MuiInputBase-inputAdornedStart:not(.MuiAutocomplete-input)': {
      paddingLeft: 0
    },
    '&.MuiInputBase-inputAdornedEnd:not(.MuiAutocomplete-input)': {
      paddingRight: 0
    }
  },
  '& .MuiFormHelperText-root': {
    lineHeight: 1.154,
    margin: theme.spacing(1, 0, 0),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    '&.Mui-error': {
      color: theme.palette.error.main
    }
  },

  // ** For Select
  '& .MuiSelect-select:focus, & .MuiNativeSelect-select:focus': {
    backgroundColor: 'transparent'
  },
  '& .MuiSelect-filled .MuiChip-root': {
    height: 22
  },

  // ** For Autocomplete
  '& .MuiAutocomplete-input': {
    paddingLeft: '6px !important',
    paddingTop: '7.5px !important',
    paddingBottom: '7.5px !important',
    '&.MuiInputBase-inputSizeSmall': {
      paddingLeft: '6px !important',
      paddingTop: '2.5px !important',
      paddingBottom: '2.5px !important'
    }
  },
  '& .MuiAutocomplete-inputRoot': {
    paddingTop: '8px !important',
    paddingLeft: '8px !important',
    paddingBottom: '8px !important',
    '&:not(.MuiInputBase-sizeSmall).MuiInputBase-adornedStart': {
      paddingLeft: '13px !important'
    },
    '&.MuiInputBase-sizeSmall': {
      paddingTop: '5px !important',
      paddingLeft: '5px !important',
      paddingBottom: '5px !important',
      '& .MuiAutocomplete-tag': {
        margin: 2,
        height: 22
      }
    }
  },

  // ** For Textarea
  '& .MuiInputBase-multiline': {
    padding: '15.25px 13px',
    '&.MuiInputBase-sizeSmall': {
      padding: '7.25px 13px'
    },
    '& textarea.MuiInputBase-inputSizeSmall:placeholder-shown': {
      overflowX: 'hidden'
    }
  },

  // ** For Date Picker
  '& + .react-datepicker__close-icon': {
    top: 11,
    '&:after': {
      fontSize: '1.6rem !important'
    }
  }
}))

const CustomTextField = forwardRef((props, ref) => {
  // ** Props
  const {
    label,
    size = 'small',
    required = false,
    InputLabelProps,
    numericValue,
    tooltipText,
    needOnBlur = false,
    ...rest
  } = props

  // State
  const [data, setData] = useState(
    props?.select ? props?.SelectProps?.value : props.value
  )

  // Handlers
  const handleKeyDown = useCallback(
    (event) => {
      if (numericValue) {
        const { key, ctrlKey, metaKey, target } = event
        const cursorPosition = target.selectionStart

        if (
          (key === '-' &&
            cursorPosition === 0 &&
            !target.value.startsWith('-')) ||
          (key === '.' && !target.value.includes('.')) ||
          key === 'Backspace' ||
          key === 'Delete' ||
          (key >= '0' && key <= '9') ||
          ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
            key
          ) ||
          ctrlKey ||
          metaKey
        ) {
          return
        }

        event.preventDefault()
      }
    },
    [numericValue]
  )

  // useEffect
  useEffect(() => {
    setData(props?.select ? props?.SelectProps?.value : props.value)
  }, [props?.select, props?.SelectProps?.value, props.value])

  return (
    <TextFieldStyled
      size={size}
      inputRef={ref}
      onKeyDown={handleKeyDown}
      {...rest}
      label={
        label ? (
          <RequiredLabelComponent
            label={label}
            error={rest?.error}
            required={required}
            needBottomMargin={false}
            tooltipText={tooltipText}
          />
        ) : undefined
      }
      onWheel={
        rest.type === 'number'
          ? (e) => {
              e.target.blur()
              rest?.onWheel && rest?.onWheel(e)
            }
          : rest?.onWheel
      }
      variant="filled"
      InputLabelProps={{ ...InputLabelProps, shrink: true }}
      {...(needOnBlur && !props.select
        ? {
            value: data,
            onChange: ({ target: { value } }) => {
              setData(value)
            },
            onBlur: () => {
              props.onChange({ target: { value: data } })
            }
          }
        : {})}
      {...(needOnBlur && props.select
        ? {
            SelectProps: {
              ...(props?.SelectProps || {}),

              value: data,
              onChange: ({ target: { value } }) => {
                setData(value)
              },
              onBlur: () => {
                props?.SelectProps?.onChange({ target: { value: data } })
              }
            }
          }
        : {})}
    />
  )
})

export default memo(CustomTextField)
